/**
 * Pure helpers for the revoke target selection.
 *
 * Extracted from the page because this mapping decides between "revoke one
 * person" and "revoke an entire customer", and `internal/frontend` has no
 * component test harness, so a helper module is the only place it can be
 * covered.
 */

/**
 * Turns the checkbox selection into the `userUuids` the mutation takes.
 *
 * An empty selection means the whole company, which the backend resolves from
 * the company itself rather than from whatever this page happened to load.
 */
export const toRevokeTargetUuids = (selected: string[]): string[] | null =>
  selected.length === 0 ? null : selected;

/**
 * Select-all toggle. Collapses to an empty selection when everything loaded is
 * already selected, which the mutation then reads as the whole company.
 */
export const toggleSelectAll = ({
  selected,
  loadedUuids,
}: {
  selected: string[];
  loadedUuids: string[];
}): string[] =>
  loadedUuids.length > 0 && selected.length === loadedUuids.length
    ? []
    : [...loadedUuids];

/**
 * Confirmation-dialog wording.
 *
 * Uses `totalCount` rather than the number of rows on screen: the list is
 * capped, so an empty selection can reach more users than are displayed, and
 * the dialog has to say so.
 */
export const describeRevokeTarget = ({
  selectedCount,
  totalCount,
  companyName,
}: {
  selectedCount: number;
  totalCount: number;
  companyName: string | null;
}): string =>
  selectedCount === 0
    ? `all ${totalCount} user(s) in ${companyName ?? "this company"}`
    : `${selectedCount} selected user(s)`;
